import { useMutation } from '@apollo/client';
import { useCallback } from 'react';

import { useCompleteFragment } from '@eluve/apollo-client';
import { SpecialtySelectorComboBox } from '@eluve/blocks';
import { useToast } from '@eluve/components';
import { useAnalytics } from '@eluve/posthog-react';
import { useUserIdFromSession } from '@eluve/session-helpers';
import { isKnownSpecialty } from '@eluve/utils';

import {
  updateUserSpecialtyMutation,
  userSpecialtyFragment,
} from './operations';

export const SpecialtySettings = () => {
  const toast = useToast();
  const userId = useUserIdFromSession();

  const userData = useCompleteFragment({
    fragment: userSpecialtyFragment,
    key: { id: userId },
  });

  const analytics = useAnalytics('user_settings');

  const currentSpecialty = userData?.specialty ?? null;

  const [updateSpecialty] = useMutation(updateUserSpecialtyMutation, {
    onError: () => {
      toast.error('Specialty could not be updated.');
    },
    optimisticResponse: (data) => ({
      updateUsersByPk: {
        __typename: 'Users' as const,
        id: userId,
        specialty: data.specialty ?? null,
      },
    }),
  });

  const handleSpecialtyChange = useCallback(
    (specialty: string | null) => {
      const isOtherSpecialty =
        specialty != null && !isKnownSpecialty(specialty);

      analytics.capture(
        isOtherSpecialty ? 'other_specialty_update' : 'specialty_update',
        {
          userId,
          currentSpecialty,
          specialty,
        },
      );

      updateSpecialty({
        variables: { userId, specialty },
      });
    },
    [analytics, userId, currentSpecialty, updateSpecialty],
  );

  return (
    <SpecialtySelectorComboBox
      selectedSpecialty={currentSpecialty}
      onSelectedSpecialty={handleSpecialtyChange}
      variant="solid"
    />
  );
};
